/**
 * Self-contained UTF-8 and base64 codecs.
 *
 * The platform provides `TextEncoder`, `btoa` and `Buffer`, but they are not uniformly
 * available across the browser main thread, workers and Node, and `btoa` is byte oriented
 * in a way that invites subtle mistakes. Owning ~60 lines here removes an entire class of
 * cross-runtime differences from the deterministic pipeline.
 */

const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

const BASE64_LOOKUP = /* @__PURE__ */ (() => {
  const table = new Int16Array(128).fill(-1);
  for (let i = 0; i < BASE64_ALPHABET.length; i += 1) {
    table[BASE64_ALPHABET.charCodeAt(i)] = i;
  }
  return table;
})();

/** Encodes a string to UTF-8 bytes. Lone surrogates become U+FFFD so output is always valid. */
export function utf8Encode(input: string): Uint8Array {
  const bytes: number[] = [];
  for (let i = 0; i < input.length; i += 1) {
    let codePoint = input.charCodeAt(i);
    if (codePoint >= 0xd800 && codePoint <= 0xdbff) {
      const next = i + 1 < input.length ? input.charCodeAt(i + 1) : 0;
      if (next >= 0xdc00 && next <= 0xdfff) {
        codePoint = (codePoint - 0xd800) * 0x400 + (next - 0xdc00) + 0x10000;
        i += 1;
      } else {
        codePoint = 0xfffd;
      }
    } else if (codePoint >= 0xdc00 && codePoint <= 0xdfff) {
      codePoint = 0xfffd;
    }

    if (codePoint < 0x80) {
      bytes.push(codePoint);
    } else if (codePoint < 0x800) {
      bytes.push(0xc0 | (codePoint >>> 6), 0x80 | (codePoint & 0x3f));
    } else if (codePoint < 0x10000) {
      bytes.push(
        0xe0 | (codePoint >>> 12),
        0x80 | ((codePoint >>> 6) & 0x3f),
        0x80 | (codePoint & 0x3f),
      );
    } else {
      bytes.push(
        0xf0 | (codePoint >>> 18),
        0x80 | ((codePoint >>> 12) & 0x3f),
        0x80 | ((codePoint >>> 6) & 0x3f),
        0x80 | (codePoint & 0x3f),
      );
    }
  }
  return Uint8Array.from(bytes);
}

export function base64Encode(bytes: Uint8Array): string {
  let out = '';
  const length = bytes.length;
  const remainder = length % 3;
  const limit = length - remainder;

  for (let i = 0; i < limit; i += 3) {
    const chunk = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
    out +=
      BASE64_ALPHABET[(chunk >>> 18) & 63] +
      BASE64_ALPHABET[(chunk >>> 12) & 63] +
      BASE64_ALPHABET[(chunk >>> 6) & 63] +
      BASE64_ALPHABET[chunk & 63];
  }

  if (remainder === 1) {
    const chunk = bytes[limit];
    out += BASE64_ALPHABET[chunk >>> 2] + BASE64_ALPHABET[(chunk << 4) & 63] + '==';
  } else if (remainder === 2) {
    const chunk = (bytes[limit] << 8) | bytes[limit + 1];
    out +=
      BASE64_ALPHABET[chunk >>> 10] +
      BASE64_ALPHABET[(chunk >>> 4) & 63] +
      BASE64_ALPHABET[(chunk << 2) & 63] +
      '=';
  }
  return out;
}

export function base64Decode(input: string): Uint8Array {
  let end = input.length;
  while (end > 0 && input[end - 1] === '=') end -= 1;

  const byteLength = Math.floor((end * 6) / 8);
  const bytes = new Uint8Array(byteLength);
  let accumulator = 0;
  let bits = 0;
  let offset = 0;

  for (let i = 0; i < end; i += 1) {
    const code = input.charCodeAt(i);
    const value = code < 128 ? BASE64_LOOKUP[code] : -1;
    if (value < 0) throw new Error(`Invalid base64 character at index ${i}`);
    accumulator = (accumulator << 6) | value;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes[offset] = (accumulator >>> bits) & 0xff;
      offset += 1;
    }
  }
  return bytes;
}

/** Packs one bit per entry, most significant bit first, row-major. */
export function packBits(values: Uint8Array): Uint8Array {
  const packed = new Uint8Array(Math.ceil(values.length / 8));
  for (let i = 0; i < values.length; i += 1) {
    if (values[i] !== 0) {
      packed[i >>> 3] |= 0x80 >>> (i & 7);
    }
  }
  return packed;
}

export function unpackBits(packed: Uint8Array, length: number): Uint8Array {
  if (packed.length < Math.ceil(length / 8)) {
    throw new Error('Packed bit array is too short');
  }
  const values = new Uint8Array(length);
  for (let i = 0; i < length; i += 1) {
    values[i] = (packed[i >>> 3] >>> (7 - (i & 7))) & 1;
  }
  return values;
}
